import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { StudioDocument } from './studio-api';
import { cover, displayTitle, documentKind, mediaUrl, needText, priceText, record, text, title } from './studio-content';
import { studioMedia } from './studio-preview-model';
import { StudioPreviewMedia } from './StudioPreviewMedia';
import { prefetchStudioPreview } from './studio-preview-store';
import { platform } from '../../platform';
import { useStudioAutoplay } from './use-studio-preferences';
import { Masonry } from '../../ui/Masonry';
import { StudioIcon } from './StudioIcon';
import { StudioGold } from './StudioGold';
import { FadeImg } from '../../ui/LateFade';
import { uiText } from '../../i18n';
const cardFormat = (item: StudioDocument) => text(item.format) || text(item.cover_format);
/** Формат плитки: подпись, значок и пропорции — одной таблицей, как FMT в bot-studio.js.
    `ar` — та же дробь, что в разметке прототипа («9/16»), `ratio` — она же числом для кладки. */
const FORMAT: Record<string, { label: string; icon: string; ratio: number; ar: string }> = {
  reels: { label: 'Reels', icon: 'video', ratio: 9 / 16, ar: '9 / 16' },
  post: { label: 'Пост', icon: 'image', ratio: 1, ar: '1 / 1' },
  photo: { label: 'Фотосессия', icon: 'sparkle', ratio: 3 / 4, ar: '3 / 4' },
  video: { label: 'Видео', icon: 'play', ratio: 16 / 9, ar: '16 / 9' },
  circle: { label: 'Кружок', icon: 'send', ratio: 1, ar: '1 / 1' },
  avatar: { label: 'Аватар', icon: 'user', ratio: 1, ar: '1 / 1' },
  slides: { label: 'Слайды', icon: 'gallery', ratio: 4 / 5, ar: '4 / 5' },
};
/** Пропорции из данных («9 / 16», «9/16», «9:16») → дробь для кладки и запись для CSS; чего не разобрать — null */
function ownRatio(value: unknown): { ratio: number; ar: string } | null {
  const match = /^\s*(\d+(?:\.\d+)?)\s*[/:]\s*(\d+(?:\.\d+)?)\s*$/.exec(typeof value === 'string' ? value : '');
  const ratio = match ? Number(match[1]) / Number(match[2]) : NaN;
  return match && Number.isFinite(ratio) && ratio >= 0.25 && ratio <= 4 ? { ratio, ar: `${match[1]} / ${match[2]}` } : null;
}
/** Плитка модуля подборки (toolTileHTML эталона) — со своими чипом, значком и формой (t.fl, t.ic, t.ar), а не по таблице
    форматов: у «Карточки для маркетплейса» чип «Всё сразу», у набора — «Набор», у слайдов и видео форма 3:4, а не 4:5 и 16:9.
    Поля ratio в контракте ToolTile пока нет (docs/03-api.md §6.2): без него форма — как у обложек модуля, 9:16 у «Рилса», иначе 3:4 */
const toolChip = (item: StudioDocument) => {
  const fallback = FORMAT[cardFormat(item)];
  const label = text(item.chip) || fallback?.label;
  return label ? { label, icon: text(item.icon) || fallback?.icon || '' } : undefined;
};
const toolShape = (item: StudioDocument) =>
  ownRatio(item.ratio) ?? ownRatio(item.ar) ?? (cardFormat(item) === 'reels' ? FORMAT.reels : FORMAT.photo);
/** Формата, которого нет в таблице, у эталона не бывает (fbadge смотрит в FMT); форма такой плитки — как у фотосессии */
const look = (item: StudioDocument) => (documentKind(item) === 'tool' ? toolChip(item) : FORMAT[cardFormat(item)]);
const shape = (item: StudioDocument) => (documentKind(item) === 'tool' ? toolShape(item) : (FORMAT[cardFormat(item)] ?? FORMAT.photo));
const cardRatio = (item: StudioDocument) => shape(item).ratio;
/** Колода позади стопки подборки: отступ над обложкой, px (#studio .st-stk .deck, studio.css) */
const DECK_PAD = 12;
/** Подпись стопки одной строкой, как collSub прототипа: «14 шаблонов · от 30 C» */
function collectionSub(item: StudioDocument): string {
  const need = needText(item);
  const price = priceText(item.price);
  return need && price ? `${need} · ${price}` : need || price;
}
/** У стопки подборки эталон держит .deck (две карточки сзади), у плитки шаблона обёртки нет вовсе. */
function Shell({ collection, children }: { collection: boolean; children: React.ReactNode }) {
  return collection ? <span className="deck">{children}</span> : <>{children}</>;
}
function StudioCard({
  item,
  onOpen,
  hero = false,
  grid = false,
  eager = false,
}: {
  item: StudioDocument;
  onOpen: (item: StudioDocument) => void;
  hero?: boolean;
  grid?: boolean;
  /** Плитка первого экрана: обложка грузится сразу, а не по подходу к окну */
  eager?: boolean;
}) {
  const [held, setHeld] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const longPress = useRef(false);
  /* загрузка документа листа — через 60 мс касания: начало прокрутки (pointercancel) её отменяет, короткое нажатие доводит сразу */
  const early = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      clearTimeout(early.current);
    },
    [],
  );
  const autoplay = useStudioAutoplay();
  const preview = record(item.preview);
  const media = studioMedia(cover(item), preview.media_type ?? preview.mime, preview.poster_url);
  const kind = documentKind(item);
  const collection = kind === 'collection';
  const tool = kind === 'tool';
  const format = cardFormat(item);
  /* у модуля подборки кружка не бывает: toolTileHTML всегда кладёт обложку .cv в форму t.ar (её же берёт кладка — cardRatio) */
  const circle = !collection && !tool && format === 'circle';
  /* число шаблонов в стопке: своё поле, список id или сам состав — на стенде подборка приходит с items */
  const count =
    typeof item.count === 'number'
      ? item.count
      : Array.isArray(item.app_ids)
        ? item.app_ids.length
        : Array.isArray(item.items)
          ? item.items.length
          : undefined;
  const chip = look(item);
  const form = shape(item);
  /* V160: обложку эталон кладёт в .pic напрямую; обёртка .cv — только у модуля подборки (st-tl)
     и у стопки, обложки которой даёт модуль (collTileHTML: c.from) */
  const wrapped = tool || (collection && !!text(item.from));
  const release = () => {
    clearTimeout(timer.current);
    clearTimeout(early.current);
    setHeld(false);
  };
  /* Обложка стопки по номеру: у эталона collPic(c,i) берёт i-й портрет подборки, при нехватке — первый */
  const covers = Array.isArray(item.covers) ? item.covers : [];
  const deck = (index: number) => mediaUrl(covers[index]) ?? (media?.type === 'image' ? media.url : null);
  /* обложка из сети: место держит .pic (aspect-ratio), догрузившаяся проявляется, а не возникает (FadeImg) */
  const picture = media ? (
    <StudioPreviewMedia
      media={media}
      held={held}
      autoplay={autoplay}
      controls={false}
      label={title(item)}
      loading={eager ? 'eager' : 'lazy'}
      fade
    />
  ) : (
    <span className="st-template-no-cover" aria-hidden="true" />
  );
  /* Чип формата стоит на плитке шаблона всегда; у ролика со своей длительностью — чип длительности (dbadge прототипа) */
  const badge =
    !collection &&
    (typeof item.duration_s === 'number' && item.duration_s > 0 ? (
      <span className="st-fb st-dur">
        <StudioIcon kind="play" />
        <span className="mono">
          {Math.floor(item.duration_s / 60)}:{String(item.duration_s % 60).padStart(2, '0')}
        </span>
      </span>
    ) : (
      chip && (
        <span className="st-fb">
          {/* у модуля подборки слово и значок свои (toolTileHTML: t.fl, t.ic — поля chip и icon), см. toolChip */}
          <StudioIcon kind={chip.icon} />
          <span>{uiText(chip.label)}</span>
        </span>
      )
    ));
  /* displayTitle: плитке шаблона и модуля название идёт через nbw, подборке — как есть (collName эталона) */
  const name = displayTitle(item);
  /* подпись плитки модуля — subtitle контракта (t.sub() эталона: «Reels · 190 C»); прежний count_text — запасной, через needText */
  const toolSub = tool ? text(item.subtitle) || needText(item) : '';
  const label = (
    <span className="tx">
      {hero && <span className="eb">{uiText('Особая подборка')}</span>}
      <b>{collection && text(item.accent) === 'gold' ? <StudioGold text={name} /> : name}</b>
      {collection || tool ? (
        <small>{collection ? collectionSub(item) : toolSub}</small>
      ) : (
        <small className="st-sub">
          <span className="nd">{needText(item)}</span>
          {/* точка-разделитель только при непустом «что нужно»: иначе подпись начиналась с неё */}
          <span className="pr">{priceText(item.price) ? `${needText(item) ? ' · ' : ''}${priceText(item.price)}` : ''}</span>
        </small>
      )}
    </span>
  );
  /* Ключ плитки — атрибуты эталона: data-app у шаблона, data-coll у стопки, data-tool у модуля
     (appTileHTML, collTileHTML, toolTileHTML); по ним смену выдачи читает use-results-transition (tileKey). */
  return (
    <button
      type="button"
      className={`${collection ? 'st-stk' : tool ? 'st-app st-tl' : 'st-app'}${hero ? ' st-chero' : ''}`}
      {...(collection ? { 'data-coll': String(item.id) } : tool ? { 'data-tool': String(item.id) } : { 'data-app': String(item.id) })}
      {...(collection || tool ? {} : { 'aria-haspopup': 'dialog' as const })}
      /* имя плитки модуля — своё, как aria-label toolTileHTML («название. подпись», название без nbw);
         без него экранный диктор собирал бы имя из alt обложки, чипа и подписи */
      {...(tool ? { 'aria-label': toolSub ? `${title(item)}. ${toolSub}` : title(item) } : {})}
      onPointerDown={() => {
        longPress.current = false;
        /* документ листа шаблона — с касания: к нажатию он обычно уже здесь, и лист выезжает заполненным (studio-preview-store) */
        if (kind === 'app') early.current = setTimeout(() => prefetchStudioPreview(item.id), 60);
        /* удержание видео-обложки — просмотр; медленное нажатие (до 450 мс) остаётся нажатием и открывает лист */
        if (media?.type === 'video' && autoplay)
          timer.current = setTimeout(() => {
            longPress.current = true;
            setHeld(true);
          }, 450);
      }}
      onPointerUp={() => {
        if (kind === 'app') prefetchStudioPreview(item.id);
        release();
      }}
      onPointerCancel={release}
      onPointerLeave={release}
      onClick={event => {
        if (event.detail !== 0 && longPress.current) return;
        /* стопка и модуль — с отдачей, как у шаблона (ui.buzz на data-coll и data-tool: impactOccurred('medium')); лист шаблона — без */
        if (collection || tool) platform.haptic('medium');
        onOpen(item);
      }}
    >
      <Shell collection={collection}>
        {collection && (
          <>
            {/* карточки сзади — вторая и третья обложки подборки (collPic(c,2), collPic(c,1)); своей нет — первая */}
            <i>{deck(2) && <FadeImg src={deck(2) ?? undefined} alt="" />}</i>
            <i>{deck(1) && <FadeImg src={deck(1) ?? undefined} alt="" />}</i>
          </>
        )}
        <span
          className={`pic${circle ? ' sq' : ''}`}
          style={{
            // в ровной сетке «Подборок» стопки одного размера, витрина — во всю ширину, у кружка подложка квадратная
            aspectRatio: hero ? '16 / 10' : circle ? '1 / 1' : grid ? '4 / 5' : form.ar,
          }}
        >
          {/* Порядок детей .pic — как в прототипе: обложка, затемнение, чип, подпись, число */}
          {circle ? (
            <>
              {media?.type === 'image' ? <FadeImg className="bg" src={media.url} alt="" /> : picture}
              <span className="ring">{media?.type === 'image' && <FadeImg src={media.url} alt="" />}</span>
              {badge}
              <span className="sh" />
              {label}
            </>
          ) : (
            <>
              {wrapped ? <span className="cv">{picture}</span> : picture}
              <span className="sh" />
              {badge}
              {label}
              {collection && count !== undefined && (
                <span className="n">
                  <StudioIcon kind="grid" />
                  {count}
                </span>
              )}
            </>
          )}
        </span>
      </Shell>
    </button>
  );
}
/** Первый экран каталога и страницы подборки — два ряда кладки под шапкой: их обложки грузятся сразу */
const FIRST_SCREEN = 4;
export function StudioCards({
  items,
  onOpen,
  grid = false,
  enterKey,
  more,
  onMore,
  eager = FIRST_SCREEN,
}: {
  items: StudioDocument[];
  onOpen: (item: StudioDocument) => void;
  /** Сколько первых плиток грузят обложку сразу, не дожидаясь подхода к окну. У ленты главной — ни одной: она ниже
      первого экрана, а лишние запросы в кадре входа главной его удлиняют (замер: возврат на главную +30 мс) */
  eager?: number;
  /** Вкладка «Подборки»: ровная сетка стопок 4:5 с витриной особой подборки первой (.st-mas.cg) */
  grid?: boolean;
  /** Смена значения — кладка проявляется заново целиком (pop ленты главной при смене формата) */
  enterKey?: string;
  /** Карточка «Смотреть все» в пустоте кладки (только лента главной, masonry эталона); высоту ставит кладка */
  more?: ReactNode;
  onMore?(shown: boolean): void;
}) {
  if (grid)
    return (
      <div className="st-mas cg">
        {items.map((item, index) => (
          <StudioCard
            key={`${documentKind(item)}:${item.id}`}
            item={item}
            onOpen={onOpen}
            hero={item.hero === true}
            grid
            eager={index < eager}
          />
        ))}
      </div>
    );
  return (
    <Masonry
      items={items}
      itemKey={item => `${documentKind(item)}:${item.id}`}
      aspectRatio={cardRatio}
      /* у стопки над обложкой колода: .deck даёт padding-top 12px (studio.css), его нет в соотношении сторон */
      extraHeight={item => (documentKind(item) === 'collection' ? DECK_PAD : 0)}
      enterKey={enterKey}
      render={item => <StudioCard item={item} onOpen={onOpen} eager={items.indexOf(item) < eager} />}
      more={more}
      onMore={onMore}
    />
  );
}
